import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';
import { Project, ProjectFormData } from '../types/project';

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  last: boolean;
  page: number;
  size: number;
}

export function useProjects(page: number = 0, size: number = 20, onlyVisible: boolean = false, category?: string) {
  return useQuery({
    queryKey: ['projects', page, size, onlyVisible, category],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      if (category) {
        params.append('category', category);
      }
      const endpoint = onlyVisible ? '/public/projects' : '/admin/projects';
      const response = await api.get<PageResponse<Project>>(`${endpoint}?${params}`);
      return response.data;
    },
    staleTime: 30000,
  });
}

export function useProject(id: number) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const response = await api.get<Project>(`/public/projects/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ProjectFormData) => {
      const data = new FormData();

      const requestData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        year: formData.year,
        category: formData.category,
        isVisible: formData.isVisible,
      };

      data.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));

      if (formData.imageFiles) {
        formData.imageFiles.forEach((file) => {
          data.append('imageFiles', file);
        });
      }

      const response = await api.post<Project>('/admin/projects', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ProjectFormData) => {
      const data = new FormData();

      const requestData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        year: formData.year,
        category: formData.category,
        isVisible: formData.isVisible,
      };

      data.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));

      if (formData.imageFiles) {
        formData.imageFiles.forEach((file) => {
          data.append('imageFiles', file);
        });
      }

      const response = await api.put<Project>(`/admin/projects/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/admin/projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProjectImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, imageId }: { projectId: number; imageId: number }) => {
      await api.delete(`/admin/projects/${projectId}/images/${imageId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
  });
}
